import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { contentAPI } from '../../services/api';
import { formatMarathiCurrency } from '../../utils/marathi';
import { MAX_CERTIFICATE_DOCUMENTS } from '../../utils/validation';
import './AdminServicesCertificates.css';

import { normalizeCustomFormFields } from '../../utils/certificateFields';
import CertificateTemplateDesigner from '../../components/admin/CertificateTemplateDesigner';

const EXEMPTION_KEYS = ['senior_citizen', 'bpl', 'disability', 'sc_st', 'widow'];

const CONFIGURATION_STEPS = [
  { id: 'basics', label: 'मूलभूत माहिती', hint: 'नाव, वर्णन आणि सेवा प्रकार' },
  { id: 'documents', label: 'कागदपत्रे', hint: 'अपलोड व ऑफलाइन फॉर्म' },
  { id: 'application', label: 'अर्ज फॉर्म', hint: 'विभाग आणि इनपुट फील्ड्स' },
  { id: 'layout', label: 'प्रमाणपत्र रचना', hint: 'लेआउट आणि live preview' },
  { id: 'publish', label: 'शुल्क व स्थिती', hint: 'शुल्क, सवलत आणि सक्रियता' },
];

const SECTION_ICON_PRESETS = [
  '📌', '🤵', '👰', '💒', '👶', '🕯️', '🏠', '💰', '📋', '🏛️', '✅', '🌾', '🏅', '➡️', '💳', '♿', '👤', '📝', '📜', '📑', '📄', '🏥', '🏢', '⚖️', '🎓', '🚗', '🔑', '💍', '👨‍👩‍👧'
];

const BLANK_FORM = {
  service_type: '',
  service_name: '',
  service_description: '',
  display_icon: '📄',
  display_category: 'प्रमाणपत्र',
  display_tone: 'blue',
  processing_time: '',
  contact_gp_for_fees: false,
  fee_amount: '0',
  duplicate_fee: '',
  service_provider: 'grampanchayat',
  required_documents: [],
  application_steps: '',
  external_office_name: '',
  external_office_address: '',
  external_contact_phone: '',
  external_website_url: '',
  public_instructions: '',
  offline_form_url: '',
  custom_form_fields: '',
  is_active: true,
  exemptions: {},
};

const fmtMoney = value => formatMarathiCurrency(value, { maximumFractionDigits: 2 });
const fmtKey = key => key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

function normalizeExemptions(exemptions) {
  if (!exemptions) return {};
  if (typeof exemptions === 'string') {
    try { return JSON.parse(exemptions) || {}; } catch { return {}; }
  }
  return exemptions || {};
}

function normalizeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(item => String(item || '').trim()).filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return normalizeList(parsed);
    } catch {
      return value.split(/\r?\n|,/).map(item => item.trim()).filter(Boolean);
    }
  }
  return [];
}

const listToText = value => normalizeList(value).join('\n');

function normalizeDocumentRequirements(value, keepEmpty = false) {
  let raw = value;
  if (typeof value === 'string') {
    try {
      raw = JSON.parse(value);
    } catch {
      raw = value.split(/\r?\n|,/);
    }
  }
  if (!Array.isArray(raw)) raw = [];

  return raw.map(item => {
    if (typeof item === 'string') return { name: item.trim(), code: item.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''), document_role: '', is_required: true };
    if (item && typeof item === 'object') {
      return {
        name: String(item.name || item.document_name || item.label || '').trim(),
        code: String(item.code || item.document_code || item.name || item.document_name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
        document_role: String(item.document_role || item.role || '').trim(),
        is_required: (item.is_required ?? item.required ?? true) !== false,
      };
    }
    return { name: '', is_required: true };
  }).filter(item => item.name || keepEmpty);
}

function buildFormFromFee(fee) {
  const rawFields = typeof fee.custom_form_fields === 'object' && fee.custom_form_fields
    ? fee.custom_form_fields
    : fee.custom_form_fields;
  const sections = normalizeCustomFormFields(rawFields);

  return {
    service_type: fee.service_type || '',
    service_name: fee.service_name || '',
    service_description: fee.service_description || '',
    display_icon: fee.display_icon || '📄',
    display_category: fee.display_category || 'प्रमाणपत्र',
    display_tone: fee.display_tone || 'blue',
    processing_time: fee.processing_time || '',
    contact_gp_for_fees: Boolean(fee.contact_gp_for_fees),
    fee_amount: String(fee.fee_amount ?? 0),
    duplicate_fee: fee.duplicate_fee == null ? '' : String(fee.duplicate_fee),
    service_provider: fee.service_provider || 'grampanchayat',
    required_documents: normalizeDocumentRequirements(fee.required_documents),
    application_steps: listToText(fee.application_steps),
    external_office_name: fee.external_office_name || '',
    external_office_address: fee.external_office_address || '',
    external_contact_phone: fee.external_contact_phone || '',
    external_website_url: fee.external_website_url || '',
    public_instructions: fee.public_instructions || '',
    offline_form_url: fee.offline_form_url || '',
    custom_form_fields: JSON.stringify(sections, null, 2),
    is_active: fee.is_active !== false,
    exemptions: normalizeExemptions(fee.exemptions),
  };
}

export default function AdminServicesCertificates() {
  const { tenantSlug } = useParams();
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [provider, setProvider] = useState('all');
  const [sort, setSort] = useState({ key: 'service_name', direction: 'asc' });
  const [useRawJson, setUseRawJson] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [wizardStep, setWizardStep] = useState(0);
  const wizardContentRef = useRef(null);
  const templateDesignerRef = useRef(null);


  const parsedSections = useMemo(() => {
    return normalizeCustomFormFields(form.custom_form_fields);
  }, [form.custom_form_fields]);

  const configuredFieldCount = useMemo(
    () => parsedSections.reduce((total, section) => total + (section.fields?.length || 0), 0),
    [parsedSections],
  );
  const wizardCompletion = [
    Boolean(form.service_type.trim() && form.service_name.trim()),
    normalizeDocumentRequirements(form.required_documents).length > 0,
    configuredFieldCount > 0,
    Boolean(editingId),
    form.is_active !== undefined,
  ];

  const updateSectionsState = newSections => {
    setForm(prev => ({
      ...prev,
      custom_form_fields: JSON.stringify(newSections, null, 2),
    }));
  };

  const addSection = () => {
    const next = [
      ...parsedSections,
      {
        section_id: `sec_${Date.now()}`,
        section_title: `विभाग ${parsedSections.length + 1}`,
        section_icon: '📌',
        fields: [],
      },
    ];
    updateSectionsState(next);
  };

  const updateSectionHeader = (secIdx, field, val) => {
    const next = parsedSections.map((sec, idx) => idx === secIdx ? { ...sec, [field]: val } : sec);
    updateSectionsState(next);
  };

  const removeSection = secIdx => {
    const next = parsedSections.filter((_, idx) => idx !== secIdx);
    updateSectionsState(next);
  };

  const addSectionField = secIdx => {
    const next = parsedSections.map((sec, idx) => {
      if (idx !== secIdx) return sec;
      return {
        ...sec,
        fields: [
          ...sec.fields,
          {
            key: `field_${Date.now().toString(36).slice(-4)}`,
            label: 'नवीन इनपुट *',
            type: 'text',
            required: true,
            is_bilingual: false,
            placeholder: '',
          },
        ],
      };
    });
    updateSectionsState(next);
  };

  const updateSectionField = (secIdx, fieldIdx, prop, val) => {
    const next = parsedSections.map((sec, sIdx) => {
      if (sIdx !== secIdx) return sec;
      const nextFields = sec.fields.map((f, fIdx) => {
        if (fIdx !== fieldIdx) return f;
        if (prop === 'options' && typeof val === 'string') {
          return { ...f, options: val.split(',').map(s => s.trim()).filter(Boolean) };
        }
        return { ...f, [prop]: val };
      });
      return { ...sec, fields: nextFields };
    });
    updateSectionsState(next);
  };

  const removeSectionField = (secIdx, fieldIdx) => {
    const next = parsedSections.map((sec, sIdx) => {
      if (sIdx !== secIdx) return sec;
      return { ...sec, fields: sec.fields.filter((_, fIdx) => fIdx !== fieldIdx) };
    });
    updateSectionsState(next);
  };

  const loadFees = async () => {
    setLoading(true);
    try {
      const res = await contentAPI.getFeesAdmin(tenantSlug);
      const nextFees = res.data.fees || [];
      setFees(nextFees);
      return nextFees;
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'सेवा माहिती लोड करता आली नाही.' });
    } finally {
      setLoading(false);
    }
    return [];
  };

  useEffect(() => {
    loadFees();
  }, [tenantSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = useMemo(() => ({
    total: fees.length,
    active: fees.filter(item => item.is_active !== false).length,
    online: fees.filter(item => (item.service_provider || 'grampanchayat') === 'grampanchayat').length,
    external: fees.filter(item => item.service_provider === 'external').length,
  }), [fees]);

  const toggleSort = key => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const SortHeader = ({ column, children }) => {
    const active = sort.key === column;
    return (
      <button
        type="button"
        className={`service-sort-header ${active ? 'active' : ''}`}
        onClick={() => toggleSort(column)}
      >
        <span>{children}</span>
        <span className="sort-icon">{active ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
      </button>
    );
  };

  const filteredFees = useMemo(() => {
    const term = query.trim().toLowerCase();
    const items = fees.filter(item => {
      const matchesText = !term || (
        (item.service_name && item.service_name.toLowerCase().includes(term)) ||
        (item.service_type && item.service_type.toLowerCase().includes(term))
      );
      const matchesStatus = status === 'all' || (status === 'active' ? item.is_active !== false : item.is_active === false);
      const matchesProvider = provider === 'all' || (provider === 'grampanchayat' ? (item.service_provider || 'grampanchayat') === 'grampanchayat' : item.service_provider === 'external');
      return matchesText && matchesStatus && matchesProvider;
    });

    return items.sort((a, b) => {
      let left, right;
      if (sort.key === 'service_name') {
        left = a.service_name || a.service_type;
        right = b.service_name || b.service_type;
      } else if (sort.key === 'service_provider') {
        left = a.service_provider || 'grampanchayat';
        right = b.service_provider || 'grampanchayat';
      } else if (sort.key === 'fee_amount') {
        left = Number(a.fee_amount || 0);
        right = Number(b.fee_amount || 0);
      } else if (sort.key === 'processing_time') {
        left = String(a.processing_time || '');
        right = String(b.processing_time || '');
      } else if (sort.key === 'is_active') {
        left = a.is_active === false ? 0 : 1;
        right = b.is_active === false ? 0 : 1;
      } else {
        left = a[sort.key];
        right = b[sort.key];
      }

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * (sort.direction === 'asc' ? 1 : -1);
      }
      const valLeft = String(left || '').toLowerCase();
      const valRight = String(right || '').toLowerCase();
      return (valLeft > valRight ? 1 : valLeft < valRight ? -1 : 0) * (sort.direction === 'asc' ? 1 : -1);
    });
  }, [fees, query, status, provider, sort]);

  const setField = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      return next;
    });
  };

  const startCreate = () => {
    setEditingId(null);
    setForm({
      ...BLANK_FORM,
      service_type: '',
      service_name: '',
      service_provider: 'grampanchayat',
      required_documents: [{ name: 'आधार कार्ड', is_required: true }, { name: 'रहिवासी पुरावा', is_required: true }],
      custom_form_fields: '[]',
    });
    setWizardStep(0);
    setShowForm(true);
  };

  const startEdit = fee => {
    setEditingId(fee.id);
    setForm(buildFormFromFee(fee));
    setWizardStep(0);
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(BLANK_FORM);
    setWizardStep(0);
  };

  const handlePdfUpload = async file => {
    if (!file) return;
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await contentAPI.uploadFile(tenantSlug, fd);
      setField('offline_form_url', res.data.url);
      setMessage({ type: 'success', text: 'ब्लँक ऑफलाईन अर्ज पीडीएफ फाईल यशस्वीरीत्या अपलोड झाली!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'फाईल अपलोड करता आली नाही.' });
    } finally {
      setUploadingPdf(false);
    }
  };

  const insertSampleFieldsJson = () => {
    const sample = [
      {
        key: 'hospital_reg_no',
        label: 'रुग्णालय नोंदणी क्रमांक',
        type: 'text',
        required: false,
        pattern: '^[A-Z0-9/-]{3,30}$',
        error_message: 'वैध नोंदणी क्रमांक टाका',
      },
      {
        key: 'ward_number',
        label: 'प्रभाग / वॉर्ड क्रमांक',
        type: 'number',
        required: true,
        min: 1,
        max: 50,
      },
    ];
    setField('custom_form_fields', JSON.stringify(sample, null, 2));
  };

  const addDocumentRequirement = () => {
    setForm(prev => ({
      ...prev,
      required_documents: normalizeDocumentRequirements(prev.required_documents, true).length >= MAX_CERTIFICATE_DOCUMENTS
        ? normalizeDocumentRequirements(prev.required_documents, true)
        : [...normalizeDocumentRequirements(prev.required_documents, true), { name: '', code: '', document_role: '', is_required: true }],
    }));
  };

  const updateDocumentRequirement = (index, field, value) => {
    setForm(prev => {
      const current = normalizeDocumentRequirements(prev.required_documents, true);
      const updated = current.map((item, idx) => idx === index ? { ...item, [field]: value } : item);
      return { ...prev, required_documents: updated };
    });
  };

  const removeDocumentRequirement = index => {
    setForm(prev => {
      const current = normalizeDocumentRequirements(prev.required_documents, true);
      return { ...prev, required_documents: current.filter((_, idx) => idx !== index) };
    });
  };

  const saveService = async (event, { stayOpen = false, nextStep = null } = {}) => {
    event?.preventDefault?.();
    const serviceType = form.service_type.trim();
    if (!serviceType || !form.service_name.trim()) {
      setMessage({ type: 'error', text: 'कृपया प्रमाणपत्र प्रकार आणि नाव प्रविष्ट करा.' });
      setWizardStep(0);
      return false;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        service_type: serviceType,
        required_documents: normalizeDocumentRequirements(form.required_documents),
      };

      // Validate JSON custom_form_fields if entered
      if (form.custom_form_fields && form.custom_form_fields.trim()) {
        try {
          payload.custom_form_fields = JSON.parse(form.custom_form_fields);
        } catch {
          setMessage({ type: 'error', text: 'अतिरिक्त कस्टम इनपुट फील्ड्स चे JSON स्वरूप अयोग्य आहे.' });
          setSaving(false);
          setWizardStep(2);
          return false;
        }
      } else {
        payload.custom_form_fields = null;
      }

      const response = await contentAPI.upsertFee(tenantSlug, payload);
      let templateSaveNote = '';
      if (wizardStep === 3 && templateDesignerRef.current?.saveDraft) {
        try {
          const templateResult = await templateDesignerRef.current.saveDraft();
          if (templateResult?.success) {
            templateSaveNote = ' व प्रमाणपत्र रचना';
          }
        } catch (templateErr) {
          console.warn('Template save error during service fee save:', templateErr);
        }
      }
      setMessage({ type: 'success', text: `प्रमाणपत्र सेवा${templateSaveNote} यशस्वीरीत्या जतन झाली!` });
      const refreshedFees = await loadFees();
      if (stayOpen) {
        const persisted = response?.data?.fee || refreshedFees.find(item => item.service_type === serviceType);
        if (persisted?.id) setEditingId(persisted.id);
        if (Number.isInteger(nextStep)) setWizardStep(nextStep);
      } else {
        resetForm();
      }
      return true;
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'सेवा जतन करता आली नाही.' });
    } finally {
      setSaving(false);
    }
    return false;
  };

  const showWizardStep = step => {
    if (step === 3 && !editingId) return;
    setWizardStep(step);
    const scrollToStep = () => wizardContentRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(scrollToStep);
    else scrollToStep();
  };

  const continueWizard = async event => {
    event.preventDefault();
    if (wizardStep === 2 && !editingId) {
      await saveService(event, { stayOpen: true, nextStep: 3 });
      return;
    }
    showWizardStep(Math.min(CONFIGURATION_STEPS.length - 1, wizardStep + 1));
  };

  const toggleActive = async fee => {
    try {
      await contentAPI.upsertFee(tenantSlug, {
        ...fee,
        is_active: fee.is_active === false,
      });
      loadFees();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'स्थिती अपडेट करता आली नाही.' });
    }
  };

  return (
    <div className="admin-services-page">
      <header className="service-page-header">
        <div>
          <h1>सेवा व प्रमाणपत्रे व्यवस्थापन</h1>
          <p>नागरिकांसाठी प्रमाणपत्रे, शुल्क, ऑफलाईन फॉर्म व मार्गदर्शक सूचना कॉन्फिगर करा.</p>
        </div>
        {showForm ? (
          <button type="button" className="service-primary-btn" style={{ background: '#475569' }} onClick={resetForm}>
            ← प्रमाणपत्र सूचीकडे परत
          </button>
        ) : (
          <button type="button" className="service-primary-btn" onClick={startCreate}>
            + नवीन सेवा जोडा
          </button>
        )}
      </header>

      {message.text && (
        <div className={`service-alert ${message.type}`}>
          <span>{message.text}</span>
          <button type="button" onClick={() => setMessage({ type: '', text: '' })}>×</button>
        </div>
      )}

      {showForm ? (
        <div className="service-inline-form-wrapper">
          <div className="service-inline-form-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button type="button" onClick={resetForm} className="service-back-btn">
                ← प्रमाणपत्र सूचीकडे परत (Back)
              </button>
              <div className="service-inline-title-box">
                <span className="service-badge">{editingId ? 'संपादन' : 'नवीन नोंद'}</span>
                <h2>{editingId ? `सेवा संपादित करा: ${form.service_name || ''}` : 'नवीन सेवा / प्रमाणपत्र जोडणी'}</h2>
              </div>
            </div>
            <div className="service-header-actions">
              <button type="button" onClick={resetForm} className="service-cancel-btn">
                रद्द करा
              </button>
              <button type="button" onClick={event => saveService(event, { stayOpen: true })} disabled={saving} className="service-save-btn">
                {saving ? 'जतन होत आहे...' : '💾 मसुदा जतन करा'}
              </button>
            </div>
          </div>

          <nav className="service-configuration-steps" aria-label="Certificate configuration steps">
            {CONFIGURATION_STEPS.map((step, index) => {
              const isLocked = index === 3 && !editingId;
              return (
                <button
                  type="button"
                  key={step.id}
                  className={`${wizardStep === index ? 'active' : ''} ${wizardCompletion[index] ? 'complete' : ''}`}
                  onClick={() => showWizardStep(index)}
                  disabled={isLocked}
                  aria-current={wizardStep === index ? 'step' : undefined}
                  aria-label={`${index + 1}. ${step.label}`}
                >
                  <span className="service-step-number">{wizardCompletion[index] ? '✓' : index + 1}</span>
                  <span className="service-step-copy">
                    <strong>{step.label}</strong>
                    <small>{isLocked ? 'प्रथम सेवा जतन करा' : step.hint}</small>
                  </span>
                </button>
              );
            })}
          </nav>

          <div className="service-step-heading" ref={wizardContentRef}>
            <div>
              <span>पायरी {wizardStep + 1} / {CONFIGURATION_STEPS.length}</span>
              <h3>{CONFIGURATION_STEPS[wizardStep].label}</h3>
              <p>{CONFIGURATION_STEPS[wizardStep].hint}</p>
            </div>
            <div className="service-step-stats" aria-label="Configuration summary">
              <span><strong>{normalizeDocumentRequirements(form.required_documents).length}</strong> कागदपत्रे</span>
              <span><strong>{configuredFieldCount}</strong> फील्ड्स</span>
              <span className={editingId ? 'saved' : ''}>{editingId ? 'मसुदा जतन आहे' : 'न जतन केलेला मसुदा'}</span>
            </div>
          </div>

          <form onSubmit={event => saveService(event, { stayOpen: true })} className="service-configuration-form">
            {(wizardStep === 0 || wizardStep === 4) && (
            <div className="service-form-grid" data-step={wizardStep}>
              <label className="service-step-0-only">
                <span>प्रमाणपत्र प्रकार *</span>
                <input
                  value={form.service_type}
                  onChange={event => setField('service_type', event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                  disabled={Boolean(editingId)}
                  placeholder="उदा. birth_certificate"
                />
              </label>

              <label className="service-step-0-only">
                <span>सेवा नाव *</span>
                <input value={form.service_name} onChange={event => setField('service_name', event.target.value)} placeholder="उदा. जन्म प्रमाणपत्र" />
              </label>

              <label className="service-step-0-only">
                <span>दर्शनी चिन्ह</span>
                <input value={form.display_icon} onChange={event => setField('display_icon', event.target.value)} maxLength={20} placeholder="📄" />
              </label>

              <label className="service-step-0-only">
                <span>दर्शनी श्रेणी</span>
                <input value={form.display_category} onChange={event => setField('display_category', event.target.value)} placeholder="प्रमाणपत्र" />
              </label>

              <label className="service-step-0-only">
                <span>रंग प्रकार</span>
                <select value={form.display_tone} onChange={event => setField('display_tone', event.target.value)}>
                  <option value="blue">निळा</option>
                  <option value="orange">केशरी</option>
                  <option value="purple">जांभळा</option>
                  <option value="green">हिरवा</option>
                  <option value="amber">पिवळा</option>
                  <option value="pink">गुलाबी</option>
                  <option value="teal">मोरपंखी</option>
                  <option value="slate">करडा</option>
                </select>
              </label>

              <label className="service-wide-field service-step-0-only">
                <span>सार्वजनिक वर्णन / माहिती</span>
                <textarea value={form.service_description} onChange={event => setField('service_description', event.target.value)} rows={2} placeholder="ही सेवा कशासाठी वापरली जाते याची नागरिकांसाठी माहिती लिहा." />
              </label>

              <label className="service-wide-field service-step-0-only">
                <span>📜 सार्वजनिक नियम व मार्गदर्शक सूचना (Citizen Guidelines)</span>
                <textarea
                  value={form.public_instructions}
                  onChange={event => setField('public_instructions', event.target.value)}
                  rows={4}
                  placeholder="नागरिकांसाठी नियम व मार्गदर्शक सूचना येथे लिहा (उदा.&#10;📄 ऑफलाईन अर्ज (Form E): विवाह नोंदणीसाठी फॉर्म 'ई' स्वहस्ते भरून जोडणे आवश्यक आहे.&#10;🎂 वय मर्यादा पात्रता: विवाह तारखेनुसार वराचे वय किमान २१ वर्षे व वधूचे वय किमान १८ वर्षे असावे.)"
                  style={{ fontFamily: 'inherit', fontSize: '0.88rem' }}
                />
                <small style={{ color: '#64748b', fontSize: '0.78rem', marginTop: 3, display: 'block' }}>
                  💡 येथे दिलेली प्रत्येक ओळ नागरिकांच्या अर्ज स्क्रीनवर मार्गदर्शक सूचना म्हणून दाखवली जाते. 'शीर्षक: मजकूर' स्वरूपात लिहिल्यास शीर्षक ठळक दिसेल.
                </small>
              </label>

              <label className="service-step-4-only">
                <span>अपेक्षित कालावधी</span>
                <input value={form.processing_time} onChange={event => setField('processing_time', event.target.value)} placeholder="उदा. ७-१० कार्यदिवस" />
              </label>

              <label className="service-step-0-only">
                <span>प्रदान प्रकार *</span>
                <select value={form.service_provider} onChange={event => setField('service_provider', event.target.value)}>
                  <option value="grampanchayat">ग्रामपंचायत प्रदान / ऑनलाइन अर्ज</option>
                  <option value="external">इतर कार्यालय / मार्गदर्शन फक्त</option>
                </select>
              </label>

              <label className="service-check service-step-4-only service-wide-field" style={{ gridColumn: '1 / -1', padding: '12px 16px', background: '#f8fafc', border: '1.5px solid #cbd5e1', borderRadius: 8, margin: '4px 0' }}>
                <input
                  type="checkbox"
                  checked={Boolean(form.contact_gp_for_fees)}
                  onChange={event => {
                    const checked = event.target.checked;
                    setForm(prev => ({
                      ...prev,
                      contact_gp_for_fees: checked,
                      fee_amount: checked ? '0' : prev.fee_amount,
                      duplicate_fee: checked ? '' : prev.duplicate_fee,
                    }));
                  }}
                />
                <span>
                  <strong style={{ color: '#1e293b' }}>शुल्क माहितीसाठी ग्रामपंचायतीशी संपर्क साधा (Contact to GP for fee details)</strong>
                  <small style={{ display: 'block', color: '#64748b', marginTop: 2 }}>
                    हा पर्याय निवडल्यास नागरिकांना निश्चित शुल्क दाखवण्याऐवजी 'ग्रामपंचायतीशी संपर्क साधा' दाखवले जाईल आणि मूळ शुल्क भरणे आवश्यक राहणार नाही.
                  </small>
                </span>
              </label>

              <label className="service-step-4-only">
                <span>मूळ शुल्क (₹)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.contact_gp_for_fees ? '' : form.fee_amount}
                  disabled={Boolean(form.contact_gp_for_fees)}
                  onChange={event => setField('fee_amount', event.target.value)}
                  placeholder={form.contact_gp_for_fees ? "ग्रामपंचायतीशी संपर्क (अमाऊंट लागू नाही)" : "0.00"}
                  style={form.contact_gp_for_fees ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed', color: '#64748b' } : {}}
                />
              </label>

              <label className="service-step-4-only">
                <span>डुप्लिकेट शुल्क (₹)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.contact_gp_for_fees ? '' : form.duplicate_fee}
                  disabled={Boolean(form.contact_gp_for_fees)}
                  onChange={event => setField('duplicate_fee', event.target.value)}
                  placeholder={form.contact_gp_for_fees ? "लागू नाही" : "लागू नसल्यास रिकामे ठेवा"}
                  style={form.contact_gp_for_fees ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed', color: '#64748b' } : {}}
                />
              </label>

              <label className="service-check service-step-4-only">
                <input type="checkbox" checked={form.is_active} onChange={event => setField('is_active', event.target.checked)} />
                <span>सार्वजनिक पोर्टलवर सक्रिय दाखवा</span>
              </label>
            </div>
            )}

            {wizardStep === 1 && (
            <div className="service-wizard-panel">
            {/* Offline Blank Form PDF Uploader */}
            <section className="service-detail-box">
              <h3>📄 ऑफलाईन अर्ज पीडीएफ फाईल (Blank Offline Form PDF)</h3>
              <p style={{ margin: '0 0 10px', fontSize: '0.82rem', color: '#64748b' }}>
                नागरिकांना स्वहस्ते भरण्यासाठी ब्लँक ऑफलाईन पीडीएफ फॉर्म (उदा. Form "E" / Form 5) येथे अपलोड करा.
              </p>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={form.offline_form_url}
                  onChange={e => setField('offline_form_url', e.target.value)}
                  placeholder="https://... फाईल URL किंवा अपलोड करा"
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.88rem' }}
                />
                <label style={{
                  padding: '8px 16px', background: uploadingPdf ? '#9ca3af' : '#1a237e', color: '#fff',
                  borderRadius: 6, cursor: uploadingPdf ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '0.82rem'
                }}>
                  {uploadingPdf ? 'अपलोड होत आहे...' : '⬆️ PDF अपलोड करा'}
                  <input
                    type="file"
                    accept=".pdf,image/*"
                    style={{ display: 'none' }}
                    onChange={e => handlePdfUpload(e.target.files[0])}
                    disabled={uploadingPdf}
                  />
                </label>
              </div>
            </section>

            <section className="service-detail-box">
              <div className="certificate-builder-heading">
                <div className="certificate-builder-icon">📎</div>
                <div>
                  <h3>आवश्यक कागदपत्रे</h3>
                  <p>नागरिकाने अपलोड करायची कागदपत्रे, त्यांचे स्थिर कोड आणि उपयोग निवडा.</p>
                </div>
              </div>
              <div className="service-document-editor">
                {normalizeDocumentRequirements(form.required_documents, true).map((doc, index) => (
                  <div className="service-document-edit-row" key={index}>
                    <span className="service-document-index">{index + 1}</span>
                    <label className="service-editor-control service-document-name">
                      <span>कागदपत्राचे नाव</span>
                      <input value={doc.name} onChange={event => updateDocumentRequirement(index, 'name', event.target.value)} placeholder="उदा. अर्जदाराचे आधार कार्ड" />
                    </label>
                    <label className="service-editor-control">
                      <span>स्थिर कोड</span>
                      <input value={doc.code || ''} onChange={event => updateDocumentRequirement(index, 'code', event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} placeholder="उदा. identity_proof" />
                    </label>
                    <label className="service-editor-control">
                      <span>कागदपत्राचा उपयोग</span>
                      <select value={doc.document_role || ''} onChange={event => updateDocumentRequirement(index, 'document_role', event.target.value)}>
                        <option value="">सामान्य कागदपत्र</option>
                        <option value="groom_photo">वराचा फोटो</option>
                        <option value="bride_photo">वधूचा फोटो</option>
                        <option value="applicant_photo">अर्जदाराचा फोटो</option>
                        <option value="identity_proof">ओळख पुरावा</option>
                        <option value="age_proof">वयाचा पुरावा</option>
                        <option value="address_proof">पत्त्याचा पुरावा</option>
                        <option value="marriage_proof">विवाह पुरावा</option>
                      </select>
                    </label>
                    <label className="service-required-toggle">
                      <input
                        type="checkbox"
                        checked={doc.is_required !== false}
                        onChange={event => updateDocumentRequirement(index, 'is_required', event.target.checked)}
                      />
                      <span><strong>आवश्यक</strong><small>अर्जासाठी बंधनकारक</small></span>
                    </label>
                    <button type="button" className="service-remove-item-btn" onClick={() => removeDocumentRequirement(index)} aria-label={`${doc.name || 'कागदपत्र'} काढा`}>🗑️ काढा</button>
                  </div>
                ))}
                <button
                  type="button"
                  className="service-add-doc-btn"
                  onClick={addDocumentRequirement}
                  disabled={normalizeDocumentRequirements(form.required_documents, true).length >= MAX_CERTIFICATE_DOCUMENTS}
                >
                  ＋ कागदपत्र जोडा ({normalizeDocumentRequirements(form.required_documents, true).length}/{MAX_CERTIFICATE_DOCUMENTS})
                </button>
              </div>
            </section>
            </div>
            )}

            {/* Section-Wise Form Fields Builder */}
            {wizardStep === 2 && (
            <section className="service-detail-box">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <h3 style={{ margin: 0, color: '#0f172a' }}>📋 फॉर्म विभाग व इनपुट फील्ड्स (Dynamic Sections & Fields)</h3>
                  <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
                    प्रमाणपत्र अर्जाचे विभाग व इनपुट क्षेत्रे कॉन्फिगर करा (उदा. १. वराचा तपशील, २. वधूचा तपशील).
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setUseRawJson(prev => !prev)}
                    style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#ffffff', color: '#334155', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}
                  >
                    {useRawJson ? '👁️ व्हिज्युअल बिल्डर' : '💻 JSON कोड दाखवा'}
                  </button>
                  <button
                    type="button"
                    onClick={addSection}
                    style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#ffffff', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 4px rgba(37,99,235,0.2)' }}
                  >
                    + नवीन विभाग जोडा
                  </button>
                </div>
              </div>

              {!useRawJson ? (
                <div className="certificate-sections-builder">
                  {parsedSections.map((sec, secIdx) => (
                    <div className="certificate-section-card" key={sec.section_id || secIdx}>
                      {/* Section Header Controls & Icon Picker */}
                      <div className="certificate-section-header">
                        <span className="certificate-section-number">{secIdx + 1}</span>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <select
                            value={SECTION_ICON_PRESETS.includes(sec.section_icon) ? sec.section_icon : 'custom'}
                            onChange={e => {
                              if (e.target.value !== 'custom') updateSectionHeader(secIdx, 'section_icon', e.target.value);
                            }}
                            style={{ padding: '6px 4px', fontSize: '1rem', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', background: '#f8fafc' }}
                            title="आयकॉन निवडा (Select Icon)"
                          >
                            {SECTION_ICON_PRESETS.map(ico => <option key={ico} value={ico}>{ico}</option>)}
                            <option value="custom">⚙️ इतर...</option>
                          </select>
                          <input
                            type="text"
                            value={sec.section_icon || '📌'}
                            onChange={e => updateSectionHeader(secIdx, 'section_icon', e.target.value)}
                            style={{ width: 42, textAlign: 'center', fontSize: '1.1rem', padding: '6px 2px', border: '1px solid #cbd5e1', borderRadius: 6 }}
                            placeholder="Icon"
                          />
                        </div>
                        <input
                          type="text"
                          value={sec.section_title}
                          onChange={e => updateSectionHeader(secIdx, 'section_title', e.target.value)}
                          placeholder="विभाग शीर्षक (उदा. १. वराचा तपशील)"
                          style={{ flex: 1, fontWeight: 700, fontSize: '0.92rem', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6 }}
                        />
                        <button
                          type="button"
                          onClick={() => removeSection(secIdx)}
                          style={{ padding: '6px 12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}
                        >
                          🗑️ विभाग हटवा
                        </button>
                      </div>

                      {/* Fields list inside section */}
                      <div className="certificate-field-list">
                        {(sec.fields || []).map((f, fieldIdx) => (
                          <div className="certificate-field-card" key={f.key || fieldIdx}>
                            <div className="certificate-field-card-head">
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>इनपुट {fieldIdx + 1}</span>
                                {f.is_bilingual && (
                                  <span style={{ fontSize: '0.68rem', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', border: '1px solid #bae6fd', borderRadius: 12, fontWeight: 700 }}>
                                    🌐 द्विभाषिक (मराठी + EN)
                                  </span>
                                )}
                              </div>
                              <button type="button" onClick={() => removeSectionField(secIdx, fieldIdx)} aria-label={`${f.label || 'इनपुट'} काढा`}>🗑️ काढा</button>
                            </div>
                            <div className="certificate-field-primary-grid">
                              <label className="service-editor-control">
                                <span>नागरिकांना दिसणारे लेबल</span>
                                <input
                                type="text"
                                value={f.label}
                                onChange={e => updateSectionField(secIdx, fieldIdx, 'label', e.target.value)}
                                placeholder="फील्ड लेबल (उदा. वराचे नाव)"
                                />
                              </label>
                              <label className="service-editor-control">
                                <span>डेटा की</span>
                                <input
                                type="text"
                                value={f.key}
                                onChange={e => updateSectionField(secIdx, fieldIdx, 'key', e.target.value)}
                                placeholder="फील्ड Key (उदा. groom_name)"
                                className="service-code-input"
                                />
                              </label>
                              <label className="service-editor-control">
                                <span>इनपुट प्रकार</span>
                                <select
                                value={f.type}
                                onChange={e => updateSectionField(secIdx, fieldIdx, 'type', e.target.value)}
                              >
                                <option value="text">Text</option>
                                <option value="number">Number</option>
                                <option value="date">Date</option>
                                <option value="select">Select (पर्याय)</option>
                                <option value="textarea">Textarea</option>
                                <option value="checkbox">Checkbox</option>
                                <option value="calculated">Calculated age</option>
                                </select>
                              </label>
                              <label className="service-editor-control certificate-required-field">
                                <span>आवश्यक फील्ड</span>
                                <span className="certificate-required-input">
                                  <input
                                    type="checkbox"
                                    checked={f.required}
                                    onChange={e => updateSectionField(secIdx, fieldIdx, 'required', e.target.checked)}
                                  />
                                  <strong>{f.required ? 'होय' : 'नाही'}</strong>
                                </span>
                              </label>
                              {['text', 'textarea'].includes(f.type || 'text') ? (
                                <label className="service-editor-control certificate-required-field certificate-bilingual-field">
                                  <span>द्विभाषिक इनपुट</span>
                                  <span className="certificate-required-input" title="मराठी व इंग्रजी दोन्ही इनपुट स्वतंत्रपणे स्वीकारणे">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(f.is_bilingual)}
                                      onChange={e => updateSectionField(secIdx, fieldIdx, 'is_bilingual', e.target.checked)}
                                    />
                                    <strong>{f.is_bilingual ? 'होय' : 'नाही'}</strong>
                                  </span>
                                </label>
                              ) : (
                                <div />
                              )}
                            </div>

                            {f.type === 'calculated' && (
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <input value={f.calculation?.date_field || ''} onChange={e => updateSectionField(secIdx, fieldIdx, 'calculation', { function: 'age_on_date', date_field: e.target.value, as_of_field: f.calculation?.as_of_field || '' })} placeholder="Date-of-birth field key" />
                                <input value={f.calculation?.as_of_field || ''} onChange={e => updateSectionField(secIdx, fieldIdx, 'calculation', { function: 'age_on_date', date_field: f.calculation?.date_field || '', as_of_field: e.target.value })} placeholder="Calculate as-of field key (e.g. marriage_date)" />
                              </div>
                            )}
                            <details className="certificate-field-advanced">
                              <summary>⚙️ प्रगत नियम व प्रमाणीकरण</summary>
                              <div className="certificate-condition-grid">
                              <input
                                defaultValue={f.visible_when ? JSON.stringify(f.visible_when) : ''}
                                onBlur={e => {
                                  try { updateSectionField(secIdx, fieldIdx, 'visible_when', e.target.value ? JSON.parse(e.target.value) : undefined); } catch { /* keep the last valid condition */ }
                                }}
                                placeholder='Visibility condition JSON: {"field":"x","operator":"equals","value":"yes"}'
                              />
                              <input
                                defaultValue={f.required_when ? JSON.stringify(f.required_when) : ''}
                                onBlur={e => {
                                  try { updateSectionField(secIdx, fieldIdx, 'required_when', e.target.value ? JSON.parse(e.target.value) : undefined); } catch { /* keep the last valid condition */ }
                                }}
                                placeholder='Required condition JSON: {"field":"x","operator":"equals","value":"yes"}'
                              />
                              </div>

                            {/* Advanced length & numeric criteria controls */}
                            <div className="certificate-validation-grid">
                              <label style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#475569', fontWeight: 600 }}>
                                <span>Min Lgth:</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={f.minLength ?? ''}
                                  onChange={e => updateSectionField(secIdx, fieldIdx, 'minLength', e.target.value === '' ? undefined : Number(e.target.value))}
                                  placeholder="उदा. 10"
                                  style={{ width: 65, padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: '0.78rem' }}
                                />
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#475569', fontWeight: 600 }}>
                                <span>Max Lgth:</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={f.maxLength ?? ''}
                                  onChange={e => updateSectionField(secIdx, fieldIdx, 'maxLength', e.target.value === '' ? undefined : Number(e.target.value))}
                                  placeholder="उदा. 12"
                                  style={{ width: 65, padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: '0.78rem' }}
                                />
                              </label>

                              {f.type === 'number' && (
                                <>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#475569', fontWeight: 600 }}>
                                    <span>Min Val:</span>
                                    <input
                                      type="number"
                                      value={f.min ?? ''}
                                      onChange={e => updateSectionField(secIdx, fieldIdx, 'min', e.target.value === '' ? undefined : Number(e.target.value))}
                                      placeholder="0"
                                      style={{ width: 65, padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: '0.78rem' }}
                                    />
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#475569', fontWeight: 600 }}>
                                    <span>Max Val:</span>
                                    <input
                                      type="number"
                                      value={f.max ?? ''}
                                      onChange={e => updateSectionField(secIdx, fieldIdx, 'max', e.target.value === '' ? undefined : Number(e.target.value))}
                                      placeholder="100"
                                      style={{ width: 65, padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: '0.78rem' }}
                                    />
                                  </label>
                                </>
                              )}
                              <input
                                type="text"
                                value={f.placeholder || ''}
                                onChange={e => updateSectionField(secIdx, fieldIdx, 'placeholder', e.target.value)}
                                placeholder="Placeholder मजकूर (उदा. 12 अंकी आधार)"
                                style={{ flex: 1, minWidth: 160, padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: '0.78rem' }}
                              />
                            </div>

                            {/* Custom Error Message Config */}
                            <div style={{ marginTop: 2 }}>
                              <input
                                type="text"
                                value={f.errorMessage || f.error_message || ''}
                                onChange={e => updateSectionField(secIdx, fieldIdx, 'errorMessage', e.target.value)}
                                placeholder="⚠️ सानुकूल त्रुटी संदेश (Custom Error Msg - उदा. वराचे पूर्ण नाव प्रविष्ट करणे आवश्यक आहे)"
                                style={{ width: '100%', padding: '4px 8px', fontSize: '0.78rem', border: '1px solid #fca5a5', background: '#fff5f5', color: '#991b1b', borderRadius: 4 }}
                              />
                            </div>
                            </details>

                            {f.type === 'select' && (
                              <div style={{ marginTop: 2 }}>
                                <input
                                  type="text"
                                  value={Array.isArray(f.options) ? f.options.join(', ') : f.options || ''}
                                  onChange={e => updateSectionField(secIdx, fieldIdx, 'options', e.target.value)}
                                  placeholder="पर्याय कॉमाने वेगळे करा (उदा. पुरुष, स्त्री, इतर)"
                                  style={{ width: '100%', padding: '5px 8px', fontSize: '0.82rem', border: '1px solid #cbd5e1', borderRadius: 6 }}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          className="certificate-add-field-btn"
                          onClick={() => addSectionField(secIdx)}
                          style={{ alignSelf: 'flex-start', marginTop: 4, padding: '6px 14px', background: '#eff6ff', border: '1px dashed #93c5fd', color: '#1d4ed8', borderRadius: 6, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}
                        >
                          + या विभागात इनपुट जोडा
                        </button>
                      </div>
                    </div>
                  ))}
                  {parsedSections.length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8, color: '#64748b', fontSize: '0.9rem' }}>
                      कोणताही विभाग जोडलेला नाही. <strong>+ नवीन विभाग जोडा</strong> बटणावर क्लिक करा.
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <button
                      type="button"
                      onClick={insertSampleFieldsJson}
                      style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}
                    >
                      ✨ नमुना JSON प्रविष्ट करा
                    </button>
                  </div>
                  <textarea
                    value={form.custom_form_fields}
                    onChange={e => setField('custom_form_fields', e.target.value)}
                    rows={10}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem', width: '100%', padding: 10, borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </div>
              )}
            </section>
            )}

            {wizardStep === 3 && (
            <div className="service-layout-step">
            <CertificateTemplateDesigner
              ref={templateDesignerRef}
              tenantSlug={tenantSlug}
              serviceType={form.service_type}
              serviceSaved={Boolean(editingId)}
              serviceProvider={form.service_provider}
            />
            </div>
            )}

            {wizardStep === 4 && (
            <section className="service-exemption-box">
              <h3>सवलती</h3>
              {form.contact_gp_for_fees ? (
                <p style={{ color: '#64748b', fontSize: '0.88rem', margin: '8px 0 0', padding: '10px 14px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                  ℹ️ शुल्क माहितीसाठी ग्रामपंचायतीशी संपर्क साधण्याचा पर्याय निवडल्याने टक्केवारी सवलती लागू होणार नाहीत.
                </p>
              ) : (
                <div>
                  {EXEMPTION_KEYS.map(key => (
                    <label key={key}>
                      <span>{fmtKey(key)} (%)</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={form.exemptions[key] ?? ''}
                        onChange={event => setForm(prev => ({ ...prev, exemptions: { ...prev.exemptions, [key]: event.target.value } }))}
                        placeholder="0"
                      />
                    </label>
                  ))}
                </div>
              )}
            </section>
            )}

            <div className={`service-wizard-actions ${wizardStep === 3 ? 'service-wizard-actions-layout' : ''}`}>
              <button
                type="button"
                onClick={() => showWizardStep(Math.max(0, wizardStep - 1))}
                disabled={wizardStep === 0}
                className="service-cancel-btn"
                aria-label="Previous configuration step"
              >
                ← मागे
              </button>
              <span>पायरी {wizardStep + 1} पैकी {CONFIGURATION_STEPS.length}</span>
              {wizardStep < CONFIGURATION_STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={continueWizard}
                  disabled={saving}
                  className="service-save-btn"
                  aria-label="Next configuration step"
                >
                  {saving
                    ? 'जतन होत आहे...'
                    : wizardStep === 2 && !editingId
                      ? 'जतन करून रचना उघडा →'
                      : 'पुढे →'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={event => saveService(event, { stayOpen: false })}
                  disabled={saving}
                  className="service-save-btn"
                >
                  {saving ? 'जतन होत आहे...' : '💾 जतन करून सूचीवर जा'}
                </button>
              )}
            </div>
          </form>
        </div>
      ) : (
        <>
          <div className="service-summary-cards">
            <div className="service-summary-card">
              <span>एकूण सेवा</span>
              <strong>{summary.total}</strong>
            </div>
            <div className="service-summary-card">
              <span>सक्रिय सेवा</span>
              <strong>{summary.active}</strong>
            </div>
            <div className="service-summary-card">
              <span>ऑनलाइन उपलब्ध</span>
              <strong>{summary.online}</strong>
            </div>
            <div className="service-summary-card">
              <span>मार्गदर्शन फक्त</span>
              <strong>{summary.external}</strong>
            </div>
          </div>

          <div className="service-layout-grid">
            <main>
              <div className="service-table-filter-bar">
                <input
                  type="search"
                  placeholder="सेवा शोधा..."
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
                <select value={status} onChange={e => setStatus(e.target.value)}>
                  <option value="all">सर्व स्थिती</option>
                  <option value="active">फक्त सक्रिय</option>
                  <option value="inactive">फक्त बंद</option>
                </select>
                <select value={provider} onChange={e => setProvider(e.target.value)}>
                  <option value="all">सर्व प्रकार</option>
                  <option value="grampanchayat">ग्रामपंचायत / ऑनलाइन</option>
                  <option value="external">मार्गदर्शन</option>
                </select>
              </div>

              <section className="service-table-card">
                {loading ? (
                  <div className="service-loading">लोड होत आहे...</div>
                ) : filteredFees.length > 0 ? (
                  <table className="service-table">
                    <thead>
                      <tr>
                        <th><SortHeader column="service_name">सेवा / प्रमाणपत्र</SortHeader></th>
                        <th><SortHeader column="service_provider">प्रकार</SortHeader></th>
                        <th><SortHeader column="fee_amount">शुल्क (₹)</SortHeader></th>
                        <th><SortHeader column="processing_time">कालावधी</SortHeader></th>
                        <th>ऑफ्लाईन फॉर्म</th>
                        <th><SortHeader column="is_active">स्थिती</SortHeader></th>
                        <th style={{ textAlign: 'right' }}>कृती</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFees.map(fee => {
                        return (
                          <tr key={fee.id || fee.service_type}>
                            <td>
                              <strong>{fee.service_name || fee.service_type}</strong>
                              <small>{fee.service_type}</small>
                            </td>
                            <td>
                              <span className={`service-tag ${(fee.service_provider || 'grampanchayat') === 'grampanchayat' ? 'gp' : 'ext'}`}>
                                {(fee.service_provider || 'grampanchayat') === 'grampanchayat' ? 'ऑनलाइन अर्ज' : 'मार्गदर्शन'}
                              </span>
                            </td>
                            <td>
                              {fee.contact_gp_for_fees ? (
                                <span style={{ color: '#0284c7', fontWeight: 600 }}>ग्रामपंचायतीशी संपर्क</span>
                              ) : (
                                fmtMoney(fee.fee_amount || 0)
                              )}
                            </td>
                            <td>{fee.processing_time || '—'}</td>
                            <td>
                              {fee.offline_form_url ? (
                                <a href={fee.offline_form_url} target="_blank" rel="noopener noreferrer" style={{ color: '#16a34a', fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none' }}>
                                  📄 PDF डाउनलोड
                                </a>
                              ) : '—'}
                            </td>
                            <td>
                              <span className={`service-status ${fee.is_active === false ? 'off' : 'on'}`}>
                                {fee.is_active === false ? 'बंद' : 'सक्रिय'}
                              </span>
                            </td>
                            <td>
                              <div className="service-actions">
                                <button type="button" onClick={() => startEdit(fee)}>संपादित</button>
                                <button type="button" onClick={() => toggleActive(fee)} className={fee.is_active === false ? 'enable' : 'disable'}>
                                  {fee.is_active === false ? 'Enable' : 'Disable'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="service-empty">निवडलेल्या फिल्टरसाठी सेवा उपलब्ध नाही.</div>
                )}
              </section>
            </main>
          </div>
        </>
      )}
    </div>
  );
}
